import {
  Badge, Button, Card, Container, Group, Loader, Stack, Table, Text, TextInput, Title,
} from "@mantine/core";
import ResponsiveTable from "../components/ResponsiveTable";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTag, IconTrash } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getTagDefinitions, createTagDefinition, deleteTagDefinition } from "../api";

const tagColors = ["blue", "teal", "green", "orange", "red", "violet", "cyan", "grape", "indigo", "pink"];

export default function AdminTagsPage() {
  const { t } = useTranslation("Veft");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("blue");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getTagDefinitions()
      .then((t) => setTags(t ?? []))
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate() {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const tag = await createTagDefinition({ name: newName.trim(), color: newColor });
      setTags((prev) => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName("");
      notifications.show({ title: "Created", message: `Tag "${tag.name}" created`, color: "green" });
    } catch {
      notifications.show({ title: "Error", message: "Failed to create tag", color: "red" });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, name) {
    try {
      await deleteTagDefinition(id);
      setTags((prev) => prev.filter((t) => t.id !== id));
      notifications.show({ title: "Deleted", message: `Tag "${name}" removed`, color: "green" });
    } catch {
      notifications.show({ title: "Error", message: "Failed to delete tag", color: "red" });
    }
  }

  if (loading) return <Loader />;

  return (
    <Container size="md" py="xl">
      <Title order={2} mb="lg">{t("tag_definitions")}</Title>

      <Card mb="lg">
        <Title order={5} mb="md">{t("add_tag_title")}</Title>
        <Group>
          <TextInput
            placeholder={t("tag_name_placeholder")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            style={{ flex: 1 }}
          />
          <Group gap={4}>
            {tagColors.map((c) => (
              <Badge
                key={c}
                size="lg"
                color={c}
                variant={newColor === c ? "filled" : "light"}
                style={{ cursor: "pointer", minWidth: 24 }}
                onClick={() => setNewColor(c)}
              >
                {" "}
              </Badge>
            ))}
          </Group>
          <Button
            leftSection={<IconPlus size={16} />}
            loading={saving}
            disabled={!newName.trim()}
            onClick={handleCreate}
          >
            {t("add")}
          </Button>
        </Group>
      </Card>

      <Card>
        <Title order={5} mb="md">{t("defined_tags", { count: tags.length })}</Title>
        {tags.length === 0 ? (
          <Text size="sm" c="dimmed">{t("no_tags_defined")}</Text>
        ) : (
          <ResponsiveTable minWidth={460}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("tag")}</Table.Th>
                <Table.Th>{t("created")}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tags.map((tag) => (
                <Table.Tr key={tag.id}>
                  <Table.Td>
                    <Badge size="lg" color={tag.color || "blue"} variant="filled">
                      {tag.name}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{new Date(tag.createdAt).toLocaleDateString()}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      variant="subtle" color="red" size="xs" p={4}
                      onClick={() => handleDelete(tag.id, tag.name)}
                    >
                      <IconTrash size={14} />
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </ResponsiveTable>
        )}
      </Card>
    </Container>
  );
}
